import React, { useState } from "react";
import MaterialIcon from "./MaterialIcon";
import ProjectPicker from "./ProjectPicker";
import ProjectConfirmModal from "./ProjectConfirmModal";
import { findExactProject, findCloseProject } from "../utils/projectMatch";
import "./AccountProjectModal.css";

/**
 * AccountProjectModal
 *
 * Shown right after a new e-mail account is connected. Asks whether the account
 * is used for one specific project/room only. If so, ALL incoming emails from
 * the account are auto-associated to that project (the account default). The
 * user can pick an existing project via the type-ahead, create a brand-new
 * (always external) room, or skip to classify each email manually.
 * Reuses the shared .email-classify-* styles.
 */
function AccountProjectModal({ account, buildings, companies = [], onConfirm, onSkip, busy, strings = {} }) {
  const [projectName, setProjectName] = useState("");
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { suggestion }
  // "link" = pick/type an existing project; "create" = new external room form.
  const [view, setView] = useState("link");
  const [newRoomName, setNewRoomName] = useState("");
  // Only ask for the company when the user is connected to more than one; a
  // single company is used implicitly, none means a personal external room.
  const [companyId, setCompanyId] = useState(companies.length === 1 ? companies[0].id : "");
  const email = account?.email || account?.user || "";
  const needsCompany = companies.length > 1;

  const submit = () => {
    if (busy) return;
    const name = projectName.trim();
    if (selectedBuildingId) {
      onConfirm({ buildingId: selectedBuildingId });
      return;
    }
    if (!name) return;
    const exact = findExactProject(name, buildings);
    if (exact) {
      onConfirm({ buildingId: exact.id });
      return;
    }
    const close = findCloseProject(name, buildings);
    if (close) {
      setConfirm({ suggestion: close });
      return;
    }
    onConfirm({ buildingName: name });
  };

  const submitNewRoom = () => {
    if (busy) return;
    const name = newRoomName.trim();
    if (!name) return;
    if (needsCompany && !companyId) return;
    onConfirm({ buildingName: name, companyId: companyId || undefined });
  };

  return (
    <div className="email-classify-overlay" role="dialog" aria-modal="true" onClick={onSkip}>
      <div className="email-classify-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-classify-header">
          <div className="email-classify-title">
            {view === "create" && (
              <button
                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral account-project-modal__back"
                onClick={() => setView("link")}
                title={strings.back || "Back"}
              >
                <MaterialIcon icon="arrow_back" size={18} />
              </button>
            )}
            <MaterialIcon icon="folder_special" size={20} color="#e066ed" />
            <span>
              {view === "create"
                ? strings.email_account_create_room || "Create new project"
                : strings.email_account_project_title || "Link account to project?"}
            </span>
          </div>
          <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral" onClick={onSkip} title={strings.cancel || "Close"}>
            <MaterialIcon icon="close" size={18} />
          </button>
        </div>

        {email && (
          <div className="email-classify-email">
            <div className="email-classify-sender">
              <span className="email-classify-sender-name">{email}</span>
            </div>
          </div>
        )}

        {view === "create" ? (
          <div className="email-classify-section">
            <div className="email-classify-question account-project-modal__label">
              {strings.email_account_project_name_placeholder || "Name"}
            </div>
            <input
              className="email-classify-input"
              type="text"
              value={newRoomName}
              placeholder={strings.email_account_project_name_placeholder || "Name"}
              disabled={busy}
              autoFocus
              autoComplete="off"
              onChange={(e) => setNewRoomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitNewRoom();
                }
              }}
            />

            {needsCompany && (
              <>
                <div className="email-classify-question account-project-modal__label--company">
                  {strings.company || "Company"}
                </div>
                <select
                  className="email-classify-input"
                  value={companyId}
                  disabled={busy}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  <option value="">{strings.email_account_create_company || "Select company"}</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            <button
              className="kj-btn kj-btn--primary kj-btn--email kj-btn--block email-classify-btn"
              onClick={submitNewRoom}
              disabled={busy || !newRoomName.trim() || (needsCompany && !companyId)}
            >
              <MaterialIcon icon="add" size={16} />
              <span>{strings.email_account_create_submit || "Create project & link"}</span>
            </button>
          </div>
        ) : (
          <>
            <div className="email-classify-section">
              <div className="email-classify-question account-project-modal__question">
                {strings.email_account_project_question ||
                  "Is this email account used exclusively for one specific project? If so, please select the project. All incoming emails will then be automatically assigned to that project."}
              </div>
              <ProjectPicker
                value={projectName}
                buildings={buildings}
                placeholder={strings.email_account_project_name_placeholder || "Name"}
                disabled={busy}
                autoFocus
                onChange={(v) => {
                  setProjectName(v);
                  setSelectedBuildingId(null);
                }}
                onSelectExisting={(b) => {
                  setProjectName(b.name);
                  setSelectedBuildingId(b.id);
                }}
                onEnter={submit}
                strings={strings}
              />
              <button
                className="kj-btn kj-btn--primary kj-btn--email kj-btn--block email-classify-btn"
                onClick={submit}
                disabled={busy || !projectName.trim()}
              >
                <MaterialIcon icon="folder" size={16} />
                <span>{strings.email_account_project_link || "Link account to project"}</span>
              </button>
              <button
                className="kj-btn kj-btn--secondary kj-btn--block email-classify-btn"
                onClick={() => {
                  setNewRoomName(projectName.trim());
                  setView("create");
                }}
                disabled={busy}
              >
                <MaterialIcon icon="add" size={16} />
                <span>{strings.email_account_create_room || "Create new project"}</span>
              </button>
            </div>

            <div className="email-classify-divider">
              <span>{strings.or || "or"}</span>
            </div>

            <div className="email-classify-section">
              <button className="kj-btn kj-btn--secondary kj-btn--block email-classify-btn" onClick={onSkip} disabled={busy}>
                <MaterialIcon icon="inbox" size={16} />
                <span>{strings.email_account_project_skip || "No, classify each email individually"}</span>
              </button>
            </div>
          </>
        )}
      </div>

      {confirm && (
        <ProjectConfirmModal
          typedName={projectName.trim()}
          suggestion={confirm.suggestion}
          onUseExisting={() => {
            setConfirm(null);
            onConfirm({ buildingId: confirm.suggestion.id });
          }}
          onCreateNew={() => {
            setConfirm(null);
            onConfirm({ buildingName: projectName.trim() });
          }}
          onClose={() => setConfirm(null)}
          strings={strings}
        />
      )}
    </div>
  );
}

export default AccountProjectModal;
